import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Loader from "@/atoms-react/loader/Loader.react";
import { Base } from "@/atoms-react/text/Text.react";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { useCheckCompanyWaitingPayment } from "@/features/company/hooks/use-company-waiting-payment";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { FromURL } from "@/features/flow/types";
import { useLinks } from "@/features/user/hooks/use-links";
import { cn } from "@/features/utils/cn";
import { useAppSelector } from "@/redux/hooks";
import { OlkyPassProTab } from "@/redux/slices/companyOnboardingSlice";
import { getRoute, OlkyPassRoutes } from "@/utils/routes";
import { LayoutTitle, MenuType } from "../_layout/index.react";
import { CompanyRelatedPerson } from "../company/components/CompanyRelatedPerson";
import { CompanyPayment } from "../company-payment/company-payment";
import { AliasesAddressApp } from "../identity/components/aliases-address/index.react";
import { AliasesDocumentsApp } from "../identity/components/aliases-documents/index.react";
import { AliasesEmailApp } from "../identity/components/aliases-email/index.react";
import { AliasMobilePage } from "../identity/components/aliases-mobile/index.react";
import { AliasesPaymentApp } from "../identity/components/aliases-payment/index.react";
import { AliasesPublicApp } from "../identity/components/aliases-public/index.react";
import { Level } from "../identity/components/level/index.react";
import { ModalValidateCompany } from "../identity/components/modal-validate-company/modal-validate-company";
import { CompanyOnboarding_Stepper } from "./components/company-onboarding-stepper";

export enum CompanyOnboardingStep {
  OlkyPass = "OlkyPass",
  RelatedPerson = "RelatedPerson",
  Payment = "Payment"
}

export const CompanyOnboarding = () => {
  const { t } = useTranslation(["pass"]);
  const { isWaitingPayment } = useCheckCompanyWaitingPayment();
  const defaultStep = isWaitingPayment
    ? CompanyOnboardingStep.Payment
    : CompanyOnboardingStep.OlkyPass;
  const currentTab = useAppSelector((state) => state.companyOnboarding.olkypassPassProTab);
  const [step, setStep] = useState<CompanyOnboardingStep>(defaultStep);
  const [validationModalIsOpen, setValidationModalIsOpen] = useState<boolean>(false);
  const [keepAnimation, setKeepAnimation] = useState<boolean>(false);
  const stepList = Object.values(CompanyOnboardingStep);
  const { leUuid } = useCompanyContext();
  const { companyDetailQuery } = useCompany();
  const { useGetLinksFromLegalEntity } = useLinks();
  const { data: linksQuery, isPending: isPendingCompaniesQuery } = useGetLinksFromLegalEntity();
  const { data: companyDetails } = companyDetailQuery(leUuid);
  const navigate = useNavigate();
  const origin = useOrigin();

  useEffect(() => {
    if (isWaitingPayment && step !== CompanyOnboardingStep.Payment) {
      setStep(CompanyOnboardingStep.Payment);
    }
  }, [isWaitingPayment]);

  useEffect(() => {
    if (companyDetails?.legalEntity.PaymentDueAmount === 0) {
      const route =
        origin === FromURL.None
          ? getRoute(OlkyPassRoutes.dashboard_company, { company: leUuid })
          : getRoute(OlkyPassRoutes.dashboard_company_partner, {
              company: leUuid,
              partner: sessionStorage.getItem("origin")
            });

      navigate(route);
    }
  }, [companyDetails]);

  const olkypassProTabs: {
    [key in OlkyPassProTab]: {
      title: string;
      comp: React.ReactNode;
    };
  } = useMemo(
    () => ({
      [OlkyPassProTab.Level]: {
        title: t("levelPage.title"),
        comp: <Level isOnboarding={true} />
      },
      [OlkyPassProTab.Documents]: {
        title: "Documents",
        comp: <AliasesDocumentsApp isOnboarding={true} />
      },
      [OlkyPassProTab.Emails]: {
        title: t("aliasEmailPage.title"),
        comp: <AliasesEmailApp isOnboarding={true} />
      },
      [OlkyPassProTab.Address]: {
        title: t("aliasAddressPage.title"),
        comp: <AliasesAddressApp isOnboarding={true} />
      },
      [OlkyPassProTab.Mobiles]: {
        title: t("aliasMobilePage.title"),
        comp: <AliasMobilePage isOnboarding={true} />
      },
      [OlkyPassProTab.Ibans]: {
        title: t("aliasPaymentsPage.title"),
        comp: <AliasesPaymentApp isOnboarding={true} />
      },
      [OlkyPassProTab.PublicVisibility]: {
        title: t("olkypass.mainPage.navigation_account_menu.link.alias_public"),
        comp: <AliasesPublicApp isOnboarding={true} />
      }
    }),
    []
  );

  const stepContent: {
    [key in CompanyOnboardingStep]: {
      title: string;
      comp: React.ReactNode;
      menuType?: MenuType | null;
    };
  } = useMemo(
    () => ({
      [CompanyOnboardingStep.OlkyPass]: {
        title: olkypassProTabs[currentTab].title,
        comp: olkypassProTabs[currentTab].comp,
        menuType: MenuType.CompanyOnboarding
      },
      [CompanyOnboardingStep.RelatedPerson]: {
        title: t("business_links.title"),
        comp: <CompanyRelatedPerson isOnBoarding={true} />
      },
      [CompanyOnboardingStep.Payment]: {
        title: t("companyPayment.title"),
        comp: <CompanyPayment canInitPayment={step === CompanyOnboardingStep.Payment} />
      }
    }),
    [currentTab, step]
  );

  const getStepIndex = (step: CompanyOnboardingStep): number =>
    Object.keys(CompanyOnboardingStep).findIndex((k) => step === k);

  const getStepByIndex = (i: number) => CompanyOnboardingStep[stepList[i]];

  const compList = Object.values(stepContent).map((sc) => sc.comp);
  const totalSteps = compList.length;
  const currentStep = stepContent[step];
  const stepIndex = getStepIndex(step);
  const carouselTranslateX = (stepIndex * 100) / totalSteps;

  const previous = () => setStep(getStepByIndex(stepIndex - 1));

  const next = (force: boolean = false) => {
    if (
      step === CompanyOnboardingStep.RelatedPerson &&
      !force &&
      !companyDetails?.legalEntity?.UserValidation
    ) {
      setKeepAnimation(true);
      setValidationModalIsOpen(true);
      return;
    }

    setStep(getStepByIndex(stepIndex + 1));
  };

  const ForceScrollToTop = () => {
    const topRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (topRef.current) {
        topRef.current.scrollIntoView({ block: "start", behavior: "instant" });
      }
    }, [currentTab]);

    return <div ref={topRef} className="pointer-events-none absolute top-0 left-0 size-0" />;
  };

  if (!companyDetails) {
    return (
      <div className="mt-10 flex w-full justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div>
      <ForceScrollToTop />

      <ModalValidateCompany
        open={validationModalIsOpen}
        onClose={(success) => {
          setValidationModalIsOpen(false);
          success && next(true);
        }}
        links={linksQuery}
      />

      <LayoutTitle menuType={isWaitingPayment ? null : (currentStep.menuType ?? null)}>
        {currentStep.title}
      </LayoutTitle>

      <CompanyOnboarding_Stepper
        step={step}
        previous={previous}
        next={next}
        nextLoading={
          step === CompanyOnboardingStep.RelatedPerson &&
          (isPendingCompaniesQuery || !companyDetails)
        }
        enableAnimation={!isWaitingPayment || keepAnimation}
      />

      {step !== CompanyOnboardingStep.Payment && (
        <div className="flex items-baseline gap-5 rounded-sm border border-primary bg-primary/10 px-5 py-3 dark:bg-primary-700">
          <div>
            <InformationCircleIcon className="size-6 text-blue-500" />
          </div>
          <Base>{t("company_onboarding.warning")}</Base>
        </div>
      )}

      <div className="relative mt-3 w-full">
        <div className="w-full overflow-hidden">
          <div
            className={cn(
              "flex",
              !isWaitingPayment ||
                (keepAnimation && "transition-transform duration-300 ease-in-out")
            )}
            style={{
              transform: `translateX(-${carouselTranslateX}%)`,
              width: `${totalSteps * 100}%`
            }}
          >
            {compList.map((c, i) => (
              <div
                key={`CompanyOnboardingComp_${i}`}
                className={`shrink-0`}
                style={{ width: `${100 / totalSteps}%` }}
              >
                {c}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
